/* The plain words the Home Wi-Fi screen says. The box judges each device
 * (cloud-anchor/pkg/homewifi) and answers codes and figures; every sentence a
 * person reads is written here, from what the box answered, and nothing here
 * claims what the box did not measure. */

export const EXPERIENCE = {
  poor: "Having a bad time",
  fair: "Struggling a little",
  good: "Doing fine",
  unknown: "Can't tell",
  idle: "Not in use",
};

/* One meaning per colour: red a bad time, amber struggling, green fine, grey
   nothing to judge. */
export const TONE = { poor: "bad", fair: "warn", good: "ok", unknown: "off", idle: "off" };

const APPS = {
  APP_NETFLIX: "Netflix", APP_YOUTUBE: "YouTube", APP_TWITCH: "Twitch", APP_AMAZON_VIDEO: "Prime Video",
  APP_DISNEYPLUS: "Disney+", APP_HULU: "Hulu", APP_HBO: "Max", APP_APPLE_TV: "Apple TV", APP_TIKTOK: "TikTok",
  APP_FACEBOOK_REEL: "Facebook", APP_SPOTIFY: "Spotify", APP_ZOOM: "Zoom", APP_GOOGLE_MEET: "Google Meet",
  APP_MSTEAMS_CALL: "Teams", APP_WHATSAPP_CALL: "WhatsApp", APP_SIGNAL_VOIP: "Signal", APP_TELEGRAM_VOIP: "Telegram",
  APP_FACEBOOK_VOIP: "Messenger", APP_VIBER_VOIP: "Viber", APP_WEBEX: "Webex", APP_SNAPCHAT_CALL: "Snapchat",
  APP_GOOGLE_CALL: "Google", APP_DISCORD: "Discord", APP_STEAM: "Steam", APP_DROPBOX: "Dropbox",
  APP_GOOGLE_DRIVE: "Google Drive", APP_APPLE_ICLOUD: "iCloud", APP_MICROSOFT: "Microsoft", APP_APPLE: "Apple",
};

export function appName(app) {
  if (!app) return "";
  if (APPS[app]) return APPS[app];
  return app.replace(/^APP_/, "").toLowerCase().replace(/(^|_)(\w)/g, (_, sep, c) => (sep ? " " : "") + c.toUpperCase());
}

/* What a device is doing, as a person would say it. */
export function doing(activity) {
  const app = appName(activity?.app);
  switch (activity?.kind) {
    case "call": return app ? `On a ${app} call` : "On a call";
    case "live": return "Playing online or on a call";
    case "video": return app ? `Watching ${app}` : "Watching video";
    case "music": return app ? `Listening on ${app}` : "Listening to music";
    case "browsing": return "Browsing";
    case "download": return app ? `Downloading from ${app}` : "Downloading";
    case "background": return "Updating in the background";
    default: return "Connected";
  }
}

/* What the activity needs, in the words the person would check against. */
function needs(activity, need) {
  if (activity?.kind === "video") return "an HD picture needs 5 and 4K needs 15";
  if (activity?.kind === "call") return `a video call needs about ${need}`;
  return `this needs about ${need}`;
}

export function deviceName(d) {
  if (d?.name) return d.name;
  const tail = (d?.mac || "").slice(-5);
  return tail ? `Unnamed device (…${tail})` : d?.ip || "Unnamed device";
}

const pct = (x) => `${Math.round(x * 100)}%`;
const mbit = (x) => (x >= 10 ? Math.round(x) : Math.round(x * 10) / 10);

/* The sentence a cause says, given the rest of the home to name another
   device by. */
export function causeSentence(c, d, others = []) {
  const v = c.values || {};
  switch (c.code) {
    case "weak_signal":
      return `Its Wi-Fi signal is weak (${v.signal_dbm} dBm): it is far from the router, or walls are in the way.`;
    case "retries":
      return `The router has to send ${pct(v.retries)} of its Wi-Fi frames to it again.`;
    case "crowded_channel":
      return `Neighbours' Wi-Fi keeps its channel busy ${pct(v.busy_others)} of the time.`;
    case "airtime_hog": {
      const o = others.find((x) => x.mac === c.device);
      return `${o ? deviceName(o) : "Another device"} is taking ${pct(v.airtime)} of the Wi-Fi's time${o?.activity?.kind === "download" ? " with a download" : ""}.`;
    }
    case "slow_link":
      return `Its Wi-Fi can carry about ${mbit(v.link_mbps)} Mbit/s right now; ${needs(d?.activity, v.need_mbps)}.`;
    case "saturated":
      return `It is using nearly all its Wi-Fi can carry: ${mbit(v.down_mbps)} of about ${mbit(v.link_mbps)} Mbit/s.`;
    case "slow_band":
      return "It is on the slower 2.4 GHz band, and it can use the box's 5 GHz network.";
    case "slow_radio":
      return `Its Wi-Fi can never carry more than about ${mbit(v.max_mbps)} Mbit/s here: an older or single-antenna radio.`;
    case "slow_inside": {
      const live = d?.activity?.kind === "call" || d?.activity?.kind === "live";
      const outside = v.outside_p95_ms != null ? `; outside the home it takes ${Math.round(v.outside_p95_ms)}` : "";
      const want = live ? ", where a call or a game wants under 30" : "";
      return `Inside the home its traffic takes up to ${Math.round(v.p95_ms)} ms to get through${v.loss > 0 ? ` and ${pct(v.loss)} is lost` : ""}${outside}${want}.`;
    }
    case "slow_outside":
      return `Outside the home its traffic takes up to ${Math.round(v.p95_ms)} ms${v.inside_p95_ms != null ? `, and inside only ${Math.round(v.inside_p95_ms)}` : ""}: the slow part is the internet line or the service.`;
    case "line_queue":
      return `The way out of the home takes ${Math.round(v.queue_ms)} ms longer than when it is quiet, so everything this device does waits.`;
    case "resent":
      return `${pct(v.resent)} of what reaches it has to be sent again: it is lost between this box and the device.`;
    case "internet":
      return v.working ? "The internet line is not working as it should." : "The internet line is down.";
    case "no_view":
      return "It is on an access point this box does not run, or on a cable, so the box cannot see its Wi-Fi.";
    case "stuck_far":
      return `It holds on to a far access point (${v.signal_dbm} dBm) while its own scan hears a nearer one of the same network at ${v.better_dbm} dBm.`;
    case "neighbours":
      return `${v.networks} other ${v.networks === 1 ? "network shares" : "networks share"} its channel, the strongest at ${v.strongest_dbm} dBm.`;
    default:
      return c.code;
  }
}

/* The one reason a row leads with: the worst cause, nearest the device. A
   note leads only a row with something to explain. */
export function leadCause(d, explain = true) {
  const order = { poor: 0, fair: 1, note: 2 };
  const causes = (d?.verdict?.causes || []).filter((c) => explain || c.severity !== "note" || c.code === "slow_band");
  causes.sort((a, b) => (order[a.severity] ?? 3) - (order[b.severity] ?? 3));
  return causes[0] || null;
}

/* Where the trouble is, as the path drawing marks it. */
export const WHERE = {
  wifi: "The trouble is its Wi-Fi link to this box.",
  home: "The trouble is inside the home, between this box and the device.",
  internet: "The trouble is the internet line, not the Wi-Fi.",
};

export function remedySentence(r, d, others = [], radios = []) {
  switch (r.code) {
    case "steer_band": {
      const to = radios.find((x) => x.name === r.target);
      return `The box can ask it to move to its ${to?.band || "faster"} network, which carries more. The device decides whether to go.`;
    }
    case "make_room": {
      const o = others.find((x) => x.mac === r.target);
      return `The box can slow ${o ? deviceName(o) : "the other device"}'s download while this one needs the room, and gives the speed back when it is done.`;
    }
    case "limit_other": {
      const o = others.find((x) => x.mac === r.target);
      return `Slow ${o ? deviceName(o) : "the other device"} down while this one needs the room: give it a speed limit on Device Speed Limits.`;
    }
    case "move_closer":
      return "Move it nearer the router, or add an access point near where it sits.";
    case "quiet_channel": {
      const on = radios.find((x) => x.name === r.target);
      return `Put the box's ${on ? `${on.band} ` : ""}Wi-Fi on a quieter channel.`;
    }
    case "client_app":
      return "If it is a phone or a computer, run the Weavian app on it so it reports its own Wi-Fi to the box.";
    case "rejoin":
      return "Turn its Wi-Fi off and on again: it should then pick the nearer access point or the faster band.";
    case "check_lines":
      return "Check the internet lines on WAN Health.";
    default:
      return r.code;
  }
}

export function actionLabel(r, radios = []) {
  if (r.code === "steer_band") {
    const to = radios.find((x) => x.name === r.target);
    return `Move it to ${to?.band || "the faster band"}`;
  }
  if (r.code === "make_room") return "Make room for it";
  return r.code;
}

export function confirmRoom(d, r, others = []) {
  const o = others.find((x) => x.mac === r.target);
  const slowed = o ? deviceName(o) : "the other device";
  return {
    title: `Make room for ${deviceName(d)}?`,
    message: `The box slows ${slowed}'s download while ${deviceName(d)} needs the room. After a minute it checks whether ` +
      `${deviceName(d)} got better: if not, it gives the speed back at once. It also gives it back once ${deviceName(d)} is done.`,
    confirmLabel: "Make room",
    variant: "primary",
  };
}

export const GIVE_BACK = "Give the speed back";

/* The make room setting, as a person chooses it. */
export const ROOM_MODES = [
  { value: "ask", label: "When someone asks" },
  { value: "auto", label: "By itself" },
  { value: "off", label: "Never" },
];

export const ROOM_EXPLAINED =
  "When a device on a call, in a game or watching something is struggling while another runs a big download, " +
  "the box can slow the download until the room is no longer needed. Each time, it checks the struggling device " +
  "got better, and gives the speed back if it did not.";

export function confirmMove(d, r, radios = []) {
  const to = radios.find((x) => x.name === r.target);
  return {
    title: `Move ${deviceName(d)} to ${to?.band || "the other band"}?`,
    message: `The box asks ${deviceName(d)} to move to its ${to?.band || "other"} network${to?.ssid ? ` "${to.ssid}"` : ""}. ` +
      "It stays connected while it moves, and it may decide to stay where it is.",
    confirmLabel: "Ask it to move",
    variant: "primary",
  };
}

/* The answer the screen leads with. */
export function headline(view) {
  const devices = view?.devices || [];
  const s = view?.summary || {};
  if (devices.length === 0) return { tone: "off", text: "No devices on the home network yet." };
  if (!s.active) return { tone: "off", text: `Nothing is in use right now. ${devices.length} ${devices.length === 1 ? "device is" : "devices are"} connected.` };
  const trouble = devices.filter((d) => d.verdict?.experience === "poor" || d.verdict?.experience === "fair");
  if (trouble.length === 0) {
    return {
      tone: "ok",
      text: s.unknown
        ? `Every device in use is doing fine, as far as the box can tell: it cannot judge ${s.unknown} of the ${s.active} from here.`
        : "Every device in use is doing fine.",
    };
  }
  const names = trouble.slice(0, 3).map(deviceName);
  const more = trouble.length > 3 ? ` and ${trouble.length - 3} more` : "";
  const list = names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0];
  return {
    tone: s.poor ? "bad" : "warn",
    text: `${trouble.length} of ${s.active} ${s.active === 1 ? "device" : "devices"} in use ${trouble.length === 1 ? "is" : "are"} having trouble: ${list}${more}.`,
  };
}

export function problemSentence(p) {
  switch (p.code) {
    case "no_radio":
      return "This box serves no Wi-Fi of its own, so it judges each device by its traffic, what it does and how quickly it gets through, rather than by its radio.";
    case "no_control":
      return `The box cannot ask devices on ${p.target} to move: the access point software does not answer for it.`;
    case "no_echoes":
      return "The box cannot echo its devices, so a game or a call over a link the box cannot see is not judged.";
    case "no_timing":
      return "The box cannot time its devices' traffic, so a device on an access point it does not run is judged by less.";
    case "radio_error":
      return "The box could not read its own radios this time; it tries again on its own.";
    case "no_counters":
      return "The box does not account its devices' traffic, so it cannot say what each is doing.";
    case "no_state":
      return "The box could not read whether it makes room by itself, so it makes room only when someone asks.";
    case "ap_unreadable":
      return `The box could not read the access point${p.target ? ` at ${p.target}` : ""}: ${p.detail || "it did not answer"}.`;
    default:
      return p.code;
  }
}

/* One radio of the home's access points, as a sentence: the box's own, or
   one the box reads over another access point's interface. */
export function radioSentence(r) {
  const net = `${r.ssid ? `"${r.ssid}"` : "A network"} on ${r.band}, channel ${r.channel}${r.width_mhz ? ` (${r.width_mhz} MHz wide)` : ""}`;
  const on = r.device ? `${r.device}: ${net}` : net;
  const who = `${r.stations} ${r.stations === 1 ? "device" : "devices"}`;
  const busy = r.busy_others != null ? ` Neighbours keep it busy ${pct(r.busy_others)} of the time${r.busy_estimated ? " (estimated)" : ""}.` : "";
  return `${on}: ${who}.${busy}`;
}

const BAND = (s) => s?.band || "its band";

function roomSentence(f, time, name) {
  const slowed = f.slowed_name || (f.slowed ? `…${f.slowed.slice(-5)}` : "another device");
  const who = f.auto ? "The box" : "Asked, the box";
  const made = `${time}: ${who} slowed ${slowed} to ${f.down_mbit} Mbit/s to make room for ${name}`;
  const went = f.before && f.after ? ` (${EXPERIENCE[f.before.experience]?.toLowerCase() || f.before.experience} before, ${EXPERIENCE[f.after.experience]?.toLowerCase() || f.after.experience} after)` : "";
  switch (f.state) {
    case "trying":
      return `${made}; checking whether it helps.`;
    case "kept":
      return `${made}: it helped${went}, so it holds while ${name} needs it.`;
    case "undone":
      return `${made}: it did not help${went}, so the box gave the speed back.`;
    case "released": {
      const why = {
        done: `${name} no longer needs the room`,
        quiet: `${slowed} finished its download`,
        expired: "it had held for half an hour",
        restart: "the box restarted",
        person: "someone asked",
      }[f.why] || "it was no longer needed";
      return `${made}. Speed given back: ${why}.`;
    }
    default:
      return `${made}.`;
  }
}

export function fixSentence(f, time) {
  const name = f.name || (f.device ? `…${f.device.slice(-5)}` : "the device");
  if (f.remedy === "make_room") return roomSentence(f, time, name);
  switch (f.state) {
    case "asked":
      return `${time}: asked ${name} to move to ${f.to}; waiting for it to answer.`;
    case "moved": {
      const gain = f.before?.link_mbps != null && f.after?.link_mbps != null
        ? `: its Wi-Fi went from about ${mbit(f.before.link_mbps)} to ${mbit(f.after.link_mbps)} Mbit/s`
        : "";
      return `${time}: ${name} moved from ${BAND(f.before)} to ${BAND(f.after)}${gain}.`;
    }
    case "stayed":
      return `${time}: asked ${name} to move; it stayed on ${BAND(f.before)}.`;
    case "left":
      return `${time}: asked ${name} to move; it left the network instead.`;
    default:
      return `${time}: ${f.state}`;
  }
}

/* The measurements behind a verdict, each a line under "How the box knows". */
export function measurements(d, radio) {
  const out = [];
  const st = d.station;
  if (st) {
    const who = radio?.device ? `the ${radio.device}` : "the box";
    if (radio) out.push(`On ${radio.device ? `the ${radio.device}'s` : "the box's"} ${radio.band} network "${radio.ssid || radio.name}", channel ${radio.channel}.`);
    if (st.signal_dbm) out.push(`Signal ${who} hears from it: ${st.signal_dbm} dBm.`);
    if (st.down?.mbps) out.push(`${who.charAt(0).toUpperCase()}${who.slice(1)} to it: ${Math.round(st.down.mbps)} Mbit/s negotiated${st.down.generation ? ` (${st.down.generation}, ${st.down.width_mhz} MHz, ${st.down.streams} ${st.down.streams === 1 ? "stream" : "streams"})` : ""}.`);
    if (d.verdict?.link_mbps != null) out.push(`Of that, about ${mbit(d.verdict.link_mbps)} Mbit/s reaches it as data right now.`);
    if (st.retries >= 0) out.push(`Frames sent again: ${pct(st.retries)} of ${st.frames}.`);
    if (st.airtime > 0) out.push(`Share of the Wi-Fi's time it took: ${pct(st.airtime)}.`);
    if (st.caps?.bands?.length) out.push(`It can use ${st.caps.bands.join(" and ")}${st.caps.steerable ? ", and it takes requests to move" : ""}.`);
  }
  const a = d.activity || {};
  if (a.kind && a.kind !== "idle") out.push(`It is pulling ${mbit(a.down_mbps)} Mbit/s from the internet and sending ${mbit(a.up_mbps)}.`);
  const c = d.client?.link;
  if (c) {
    const on = [c.band, c.channel ? `channel ${c.channel}` : ""].filter(Boolean).join(", ");
    out.push(`Its own app reports: ${c.signal_dbm} dBm${on ? ` on ${on}` : ""}${c.rx?.mbps ? `, receiving at ${Math.round(c.rx.mbps)} Mbit/s` : ""}${c.standard ? ` (${c.standard})` : ""}.`);
  }
  if (d.client?.heard?.length) out.push(`Its own scan hears ${d.client.heard.length} ${d.client.heard.length === 1 ? "network" : "networks"} around it.`);
  if (d.client?.probe?.answered) out.push(`Its own trips to the box: ${Math.round(d.client.probe.p50_ms)} ms typical, ${Math.round(d.client.probe.p95_ms)} ms at worst.`);
  const t = d.timing;
  if (t?.inside) out.push(`Its traffic inside the home, timed by the box: ${Math.round(t.inside.p50_ms)} ms typical, ${Math.round(t.inside.p95_ms)} ms at worst (${t.inside.samples} timings).`);
  if (t?.outside) out.push(`Outside the home: ${Math.round(t.outside.p50_ms)} ms typical, ${Math.round(t.outside.p95_ms)} ms at worst (${t.outside.samples} timings).`);
  if (t?.resent >= 0) out.push(`Sent to it again after the box had passed it on: ${pct(t.resent)} of ${t.segments}.`);
  const l = d.latency;
  if (l?.answered) out.push(`The box's echoes to it: ${Math.round(l.p50_ms)} ms typical, ${Math.round(l.p95_ms)} ms at worst, ${l.sent - l.answered} of ${l.sent} lost.`);
  else if (l?.sent) out.push(`It did not answer the box's echoes (${l.sent} sent), which some devices never do.`);
  return out;
}

/* The coverage map from a walk through the home. */
export const MAP_NONE =
  "Walk the home with the Weavian app to map its Wi-Fi: start beside the router, walk through every room at an " +
  "easy pace, and mark where the television and the other devices that stay put sit.";

/* Signal in the colours the map uses: one meaning each. */
export function signalTone(dbm) {
  if (dbm == null) return "none";
  if (dbm >= -60) return "strong";
  if (dbm >= -67) return "good";
  if (dbm >= -70) return "fair";
  return "weak";
}

export const SIGNAL_WORDS = {
  strong: "Strong: anything works",
  good: "Good: 4K and calls hold",
  fair: "Fair: HD plays, calls may break up",
  weak: "Weak: streams buffer, calls break up",
};

const pctWord = (x) => `${Math.round(x * 100)}%`;

export function mapHeadline(a) {
  if (!a) return "";
  if (a.problems?.includes("no_network")) return "The walk heard no network the phone was on, so there is nothing to map.";
  if (!a.aps?.length) return "The walk was too short to place the access point. Walk a little longer, through more rooms.";
  const weak = a.weak_share > 0 ? `${pctWord(a.weak_share)} of the places walked are weak.` : "Every place walked gets a good signal.";
  const units = new Set(a.aps.map((x) => x.unit)).size;
  const placed = units === 1 ? "The walk placed the access point" : `The walk placed ${units} access points`;
  return `${placed} and mapped the signal around it. ${weak}`;
}

export function markSentence(m) {
  if (m.dbm == null) return `${m.label}: the walk did not reach it.`;
  const tone = signalTone(m.dbm);
  const how = { strong: "a strong signal", good: "a good signal", fair: "a fair signal", weak: "a weak signal" }[tone];
  const ext = m.with_extender != null && m.with_extender > m.dbm + 2 ? `; with the extender, about ${Math.round(m.with_extender)} dBm` : "";
  return `${m.label}: ${how} (${Math.round(m.dbm)} dBm${m.measured ? "" : ", expected"})${ext}.`;
}

export function extenderSentence(e) {
  if (!e) return "";
  const near = e.near ? ` about ${e.near_m} m from ${e.near}` : "";
  const after = e.weak_after > 0
    ? `It would leave ${pctWord(e.weak_after)} of the places walked weak, where ${pctWord(e.weak_before)} are today.`
    : `It would leave no place walked weak, where ${pctWord(e.weak_before)} are today.`;
  return `Put an extender at the star${near}, where the router still reaches it at ${Math.round(e.backhaul_dbm)} dBm. ${after}`;
}

/* The fleet's view: homes in counts, no one named. */
const REASON = {
  weak_signal: "a weak signal", slow_link: "a link too slow for what it does", crowded_channel: "a crowded channel",
  airtime_hog: "another device taking the Wi-Fi", saturated: "a full link", retries: "frames sent again",
  slow_inside: "a slow path inside the home", resent: "data lost inside the home", slow_outside: "a slow path outside the home",
  internet: "the internet line", line_queue: "a slow way out of the home", stuck_far: "devices holding on to a far access point", neighbours: "neighbours' networks",
};
export const reasonWord = (code) => REASON[code] || code;

const WHERE_SHORT = { wifi: "the Wi-Fi", home: "inside the home", internet: "the internet line" };

const top = (m) => Object.entries(m || {}).sort((a, b) => b[1] - a[1])[0]?.[0];

export function fleetHeadline(customers) {
  const homes = (customers || []).flatMap((c) => c.homes || []).filter((h) => !h.old);
  if (homes.length === 0) return { tone: "off", text: "No home has reported its Wi-Fi yet." };
  const struggling = homes.filter((h) => h.struggling > 0);
  if (struggling.length === 0) {
    return { tone: "ok", text: `No device is struggling in any of the ${homes.length} ${homes.length === 1 ? "home" : "homes"} right now.` };
  }
  const where = {};
  for (const h of struggling) {
    const w = top(h.report.where);
    if (w) where[w] = (where[w] || 0) + 1;
  }
  const parts = Object.entries(where).sort((a, b) => b[1] - a[1]).map(([w, n]) => `in ${n} the trouble is ${WHERE_SHORT[w] || w}`);
  return {
    tone: struggling.some((h) => h.report.summary.poor > 0) ? "bad" : "warn",
    text: `${struggling.length} of ${homes.length} homes have a device struggling right now${parts.length ? `: ${parts.join(", ")}` : ""}.`,
  };
}

export function homeSentence(h) {
  const s = h.report.summary;
  if (!s.active) return `Nothing in use right now; ${s.idle} ${s.idle === 1 ? "device" : "devices"} connected.`;
  if (!h.struggling) return `All ${s.active} ${s.active === 1 ? "device" : "devices"} in use are doing fine.`;
  // The reason says where it is; the place alone stands in when no reason led.
  const why = top(h.report.causes);
  const where = top(h.report.where);
  const said = why ? reasonWord(why) : WHERE_SHORT[where] || "";
  return `${h.struggling} of ${s.active} devices in use ${h.struggling === 1 ? "is" : "are"} struggling${said ? `: ${said}` : ""}.`;
}

export function homeExtras(h) {
  const out = [];
  if (h.report.rooms) out.push(`The box is making room for ${h.report.rooms === 1 ? "a device" : `${h.report.rooms} devices`}.`);
  const sv = h.report.survey;
  if (sv) out.push(sv.extender ? `Its last walk found ${Math.round(sv.weak_share * 100)}% of the home weak and an extender spot.` : "Its last walk found no weak spot.");
  if (h.report.reporting) out.push(`${h.report.reporting} ${h.report.reporting === 1 ? "device reports" : "devices report"} through the Weavian app.`);
  return out;
}
